import { useSignal } from "@preact/signals";
import {
  Button,
  type MemberOption,
  MemberPicker,
  RadioCard,
  RadioCardContent,
} from "../../../../components/ui";
import type { Group } from "../../../../types";

type AssignmentMode = "all" | "non_admins" | "groups" | "choose" | null;

type ServerAssignmentMode = "all" | "non_admins" | "choose" | null;

type AssignmentPanelProps = {
  assignedMemberIds: string[];
  excludedMemberIds: string[];
  members: MemberOption[];
  groups: Group[];
  groupMemberships: Map<string, string[]>; // groupId -> memberIds
  currentMemberId: string;
  onChange: (
    memberIds: string[],
    mode: ServerAssignmentMode,
    excludedIds?: string[],
  ) => void;
  onClose: () => void;
};

export function AssignmentPanel({
  assignedMemberIds,
  excludedMemberIds,
  members,
  groups,
  groupMemberships,
  currentMemberId,
  onChange,
  onClose,
}: AssignmentPanelProps) {
  // Helper to get members for selected groups
  const getMembersForGroups = (groupIds: string[]): string[] => {
    const memberSet = new Set<string>();
    for (const groupId of groupIds) {
      const groupMembers = groupMemberships.get(groupId) ?? [];
      for (const memberId of groupMembers) {
        memberSet.add(memberId);
      }
    }
    return Array.from(memberSet);
  };

  // Derive initial mode from current assignments
  const deriveMode = (memberIds: string[]): AssignmentMode => {
    if (memberIds.length === 0 && excludedMemberIds.length === 0) return null;

    const excludedSet = new Set(excludedMemberIds);
    const allMemberIds = new Set(
      members.filter((m) => !excludedSet.has(m.id)).map((m) => m.id),
    );
    const nonAdminIds = new Set(
      members
        .filter((m) => m.role !== "admin" && !excludedSet.has(m.id))
        .map((m) => m.id),
    );
    const selectedSet = new Set(memberIds);

    if (
      allMemberIds.size === selectedSet.size &&
      [...allMemberIds].every((id) => selectedSet.has(id))
    ) {
      return "all";
    }

    if (
      nonAdminIds.size > 0 &&
      nonAdminIds.size === selectedSet.size &&
      [...nonAdminIds].every((id) => selectedSet.has(id))
    ) {
      return "non_admins";
    }

    // Check if selection matches any combination of groups
    if (groups.length > 0) {
      for (const group of groups) {
        const groupMembers = groupMemberships.get(group.id) ?? [];
        if (
          groupMembers.length > 0 &&
          groupMembers.length === selectedSet.size &&
          groupMembers.every((id) => selectedSet.has(id))
        ) {
          return "groups";
        }
      }
    }

    return "choose";
  };

  // Derive initial selected groups from member IDs
  const deriveSelectedGroups = (memberIds: string[]): string[] => {
    const selectedSet = new Set(memberIds);
    const matchingGroups: string[] = [];

    for (const group of groups) {
      const groupMembers = groupMemberships.get(group.id) ?? [];
      if (
        groupMembers.length > 0 &&
        groupMembers.every((id) => selectedSet.has(id))
      ) {
        matchingGroups.push(group.id);
      }
    }

    return matchingGroups;
  };

  // Local state
  const localMode = useSignal<AssignmentMode>(deriveMode(assignedMemberIds));
  const localSelectedIds = useSignal<string[]>(assignedMemberIds);
  const localSelectedGroupIds = useSignal<string[]>(
    deriveSelectedGroups(assignedMemberIds),
  );
  const localExcludedIds = useSignal<string[]>(excludedMemberIds);

  // Map UI mode to the server-side assignment_mode
  const toServerMode = (mode: AssignmentMode): ServerAssignmentMode => {
    if (mode === "all" || mode === "non_admins") return mode;
    if (mode === "groups" || mode === "choose") return "choose";
    return null;
  };

  const setMode = (mode: AssignmentMode) => {
    localMode.value = mode;
    let newIds: string[] = [];

    if (mode === "all") {
      newIds = members
        .filter((m) => !localExcludedIds.value.includes(m.id))
        .map((m) => m.id);
      localSelectedGroupIds.value = [];
    } else if (mode === "non_admins") {
      newIds = members
        .filter(
          (m) => m.role !== "admin" && !localExcludedIds.value.includes(m.id),
        )
        .map((m) => m.id);
      localSelectedGroupIds.value = [];
    } else if (mode === "groups") {
      // Keep existing group selection or start empty
      newIds = getMembersForGroups(localSelectedGroupIds.value);
      localExcludedIds.value = [];
    } else if (mode === null) {
      newIds = [];
      localSelectedGroupIds.value = [];
      localExcludedIds.value = [];
    } else {
      // For "choose", keep existing selection
      newIds = localSelectedIds.value;
      localSelectedGroupIds.value = [];
      localExcludedIds.value = [];
    }

    localSelectedIds.value = newIds;
    onChange(newIds, toServerMode(mode), localExcludedIds.value);
  };

  const setSelectedIds = (ids: string[]) => {
    localSelectedIds.value = ids;
    onChange(ids, toServerMode(localMode.value), localExcludedIds.value);
  };

  const setSelectedGroupIds = (groupIds: string[]) => {
    localSelectedGroupIds.value = groupIds;
    const memberIds = getMembersForGroups(groupIds);
    localSelectedIds.value = memberIds;
    onChange(memberIds, toServerMode(localMode.value), localExcludedIds.value);
  };

  const setExcludedIds = (ids: string[]) => {
    localExcludedIds.value = ids;
    // Recompute assigned members excluding newly excluded ones
    const mode = localMode.value;
    let newIds: string[] = [];
    if (mode === "all") {
      newIds = members.filter((m) => !ids.includes(m.id)).map((m) => m.id);
    } else if (mode === "non_admins") {
      newIds = members
        .filter((m) => m.role !== "admin" && !ids.includes(m.id))
        .map((m) => m.id);
    }
    localSelectedIds.value = newIds;
    onChange(newIds, toServerMode(mode), ids);
  };

  const nonAdminCount = members.filter((m) => m.role !== "admin").length;

  return (
    <div class="flex flex-col gap-6">
      <fieldset class="flex flex-col gap-2">
        <legend class="text-sm font-medium text-text-secondary mb-2">
          Who should fill out this form?
        </legend>

        <RadioCard
          name="assignTo"
          value=""
          checked={localMode.value === null}
          onChange={() => setMode(null)}
        >
          <RadioCardContent
            label="Not assigned"
            description="Form is a draft, not assigned to anyone"
          />
        </RadioCard>
        <RadioCard
          name="assignTo"
          value="all"
          checked={localMode.value === "all"}
          onChange={() => setMode("all")}
        >
          <RadioCardContent
            label="Everyone"
            description={
              localExcludedIds.value.length > 0
                ? `${members.length - localExcludedIds.value.length} of ${members.length} members (${localExcludedIds.value.length} excluded)`
                : `All ${members.length} members (including admins)`
            }
          />
        </RadioCard>
        <RadioCard
          name="assignTo"
          value="non_admins"
          checked={localMode.value === "non_admins"}
          onChange={() => setMode("non_admins")}
        >
          <RadioCardContent
            label="Non-admins only"
            description={
              localMode.value === "non_admins" &&
              localExcludedIds.value.length > 0
                ? `${nonAdminCount - localExcludedIds.value.length} of ${nonAdminCount} non-admin members (${localExcludedIds.value.length} excluded)`
                : `${nonAdminCount} members who are not admins`
            }
          />
        </RadioCard>
        {groups.length > 0 && (
          <RadioCard
            name="assignTo"
            value="groups"
            checked={localMode.value === "groups"}
            onChange={() => setMode("groups")}
          >
            <RadioCardContent
              label="Specific groups"
              description="Select one or more groups"
            />
          </RadioCard>
        )}
        <RadioCard
          name="assignTo"
          value="choose"
          checked={localMode.value === "choose"}
          onChange={() => setMode("choose")}
        >
          <RadioCardContent
            label="Choose specific people"
            description="Select individual members"
          />
        </RadioCard>
      </fieldset>

      {/* Group selection */}
      {localMode.value === "groups" && (
        <GroupPicker
          groups={groups}
          groupMemberships={groupMemberships}
          selectedIds={localSelectedGroupIds.value}
          onChange={setSelectedGroupIds}
        />
      )}

      {/* Individual member selection */}
      {localMode.value === "choose" && (
        <MemberPicker
          members={members}
          selectedIds={localSelectedIds.value}
          onChange={setSelectedIds}
          currentMemberId={currentMemberId}
        />
      )}

      {/* Excluded members (for "all" or "non_admins" modes) */}
      {(localMode.value === "all" || localMode.value === "non_admins") && (
        <div class="flex flex-col gap-2">
          <p class="text-sm font-medium text-text-secondary">
            Exclude specific people
          </p>
          <p class="text-sm text-text-muted">
            These members won't be assigned or receive reminders.
          </p>
          <MemberPicker
            members={members}
            selectedIds={localExcludedIds.value}
            onChange={setExcludedIds}
            currentMemberId={currentMemberId}
          />
        </div>
      )}

      {/* Done button */}
      <div class="sticky bottom-0 -mx-6 px-6 py-4 border-t border-border bg-surface">
        <Button type="button" variant="ghost" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// GroupPicker component
// ============================================================================

type GroupPickerProps = {
  groups: Group[];
  groupMemberships: Map<string, string[]>;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
};

function GroupPicker({
  groups,
  groupMemberships,
  selectedIds,
  onChange,
}: GroupPickerProps) {
  const selectedSet = new Set(selectedIds);

  const toggle = (groupId: string) => {
    if (selectedSet.has(groupId)) {
      onChange(selectedIds.filter((id) => id !== groupId));
    } else {
      onChange([...selectedIds, groupId]);
    }
  };

  // Calculate total unique members across selected groups
  const totalMembers = new Set<string>();
  for (const groupId of selectedIds) {
    const members = groupMemberships.get(groupId) ?? [];
    for (const memberId of members) {
      totalMembers.add(memberId);
    }
  }

  return (
    <div class="flex flex-col gap-3">
      <div class="flex flex-wrap gap-2">
        {groups.map((group) => {
          const isSelected = selectedSet.has(group.id);
          const memberCount = groupMemberships.get(group.id)?.length ?? 0;
          return (
            <button
              key={group.id}
              type="button"
              onClick={() => toggle(group.id)}
              class={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isSelected
                  ? "bg-text text-surface"
                  : "bg-surface-hover text-text-muted hover:text-text hover:bg-surface-active"
              }`}
            >
              {isSelected && (
                <svg
                  class="size-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
              {group.name}
              <span class={isSelected ? "opacity-70" : "text-text-subtle"}>
                ({memberCount})
              </span>
            </button>
          );
        })}
      </div>
      {selectedIds.length > 0 && (
        <p class="text-sm text-text-muted">
          {totalMembers.size} {totalMembers.size === 1 ? "person" : "people"}{" "}
          will receive this form
        </p>
      )}
      {selectedIds.length === 0 && (
        <p class="text-sm text-amber-600 dark:text-amber-400">
          Select at least one group
        </p>
      )}
    </div>
  );
}
